import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  subscribePromptDialog,
  type PromptDialogRequest,
} from "@/lib/promptDialog";

/**
 * Singleton host that renders the shared prompt dialog triggered by
 * {@link promptDialog}. Same contract as `window.prompt` — resolves with
 * the entered string on validation, or `null` when the user cancels /
 * closes the dialog.
 */
export default function PromptDialogHost() {
  const [req, setReq] = useState<PromptDialogRequest | null>(null);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => subscribePromptDialog(setReq), []);

  // Reset the field whenever a new request lands.
  useEffect(() => {
    if (req) {
      setValue(req.defaultValue ?? "");
      setError(null);
      // Focus + select the input on the next tick.
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [req?.id]);

  const open = req !== null;

  const handleOpenChange = (v: boolean) => {
    if (!v && req) req.resolve(null);
  };

  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!req) return;
    const trimmed = value;
    if (req.validate) {
      const err = req.validate(trimmed);
      if (err) {
        setError(err);
        return;
      }
    }
    req.resolve(trimmed);
  };

  const inputType = req?.inputType ?? "text";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>{req?.title}</DialogTitle>
          {req?.description && (
            <DialogDescription>{req.description}</DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 overflow-y-auto flex-1 min-h-0 pr-1">
          {req?.label && (
            <Label htmlFor="prompt-dialog-input" className="text-sm">
              {req.label}
            </Label>
          )}
          <Input
            id="prompt-dialog-input"
            ref={inputRef}
            type={inputType}
            value={value}
            placeholder={req?.placeholder}
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError(null);
            }}
            aria-invalid={!!error}
          />
          {error && (
            <p className="text-xs text-destructive" role="alert">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => req?.resolve(null)}
            >
              {req?.cancelLabel ?? "Annuler"}
            </Button>
            <Button type="submit">
              {req?.confirmLabel ?? "OK"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
